import React, { PropsWithChildren, useState } from 'react'
import { useDebounce } from 'ahooks'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useAssignRequirementToPositionMutation } from 'queries/company'
import { useFuzzySearchRequirementsQuery } from 'queries/requirements'
import { usePositionDetailContextValue } from '../../../context/PositionDetailProvider'

const SEARCH_RESULT_LIMIT = 100
const SEARCH_DEBOUNCE_MS = 300

type RequirementSearchResult = NonNullable<
  ReturnType<typeof useFuzzySearchRequirementsQuery>['data']
>[number]

type RequirementOption = {
  label: string
  value: number
  requirement: RequirementSearchResult
}

type AddRequirementModalContextState = {
  state: {
    search: string
    options: RequirementOption[]
    isSearchFetching: boolean
    existingRequirementIds: number[]
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    setSearch: (search: string) => void
    submit: ReturnType<typeof useAssignRequirementToPositionMutation>['mutate']
    reset: () => void
  }
}

const [AddRequirementModalContext, useAddRequirementModalContextValue] =
  createContext<AddRequirementModalContextState>({
    name: 'AddRequirementModalContext',
  })

export const AddRequirementModalProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    state: { id: positionId, requirements },
  } = usePositionDetailContextValue()
  const {
    mutate,
    reset: resetMutation,
    isPending,
    isError,
    error,
  } = useAssignRequirementToPositionMutation(positionId)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, {
    wait: search.trim() === '' ? 0 : SEARCH_DEBOUNCE_MS,
  })

  const existingRequirementIds = requirements.map(
    (requirement) => requirement.id
  )
  const assignedRequirementIds = new Set(existingRequirementIds)

  const { data: searchResults, isFetching: isSearchFetching } =
    useFuzzySearchRequirementsQuery({
      search: debouncedSearch,
      limit: SEARCH_RESULT_LIMIT,
    })

  const options = (searchResults ?? [])
    .filter((requirement) => !assignedRequirementIds.has(requirement.id))
    .map(
      (requirement): RequirementOption => ({
        label: `(${requirement.id}) ${requirement.name}`,
        value: requirement.id,
        requirement,
      })
    )

  const reset = () => {
    setSearch('')
    resetMutation()
  }

  const value: AddRequirementModalContextState = {
    state: {
      search,
      options,
      isSearchFetching,
      existingRequirementIds,
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
    actions: {
      setSearch,
      submit: mutate,
      reset,
    },
  }

  return (
    <AddRequirementModalContext.Provider value={value}>
      {children}
    </AddRequirementModalContext.Provider>
  )
}

export { useAddRequirementModalContextValue }
